import { ActionContext } from "vuex/types";
import { getListFileDraft } from "@/services/api/process/ApiFile";
import {
  setSignatureRequest,
  setExternalSignatureRequest
} from "@/services/api/process/ApiSignature";
import { SnackToast as Toast } from "@/utils/snackToast";
import { IUser } from "@/store/types/user-types";
import {
  IMessageObject,
  ResponseGenericType,
  ResponseType
} from "@/store/types/types";
import {
  ICheckParams,
  IExternalSubscriberRequest,
  IInternalSignature,
  ISignatureDraftList
} from "@/store/types/signature-type";
import {
  IDraftDocumentRequest,
  IProcessFileComplete
} from "@/store/types/file-type";
import { General } from "@/utils/constants";
import { RootState } from "@/store/types/root";

const state = {
  subscribers: [] as IUser[],
  listFileDraft: [] as IProcessFileComplete[],
  error: false as boolean
};

type State = typeof state;

const mutations = {
  clearError: (state: State) => {
    state.error = false;
  },

  postSubscriberError: (
    state: State,
    message: string | string[] | IMessageObject
  ) => {
    state.error = Boolean(message);

    if (Array.isArray(message))
      message.forEach(message =>
        Toast.open({
          message: message,
          type: "is-warning",
          position: "is-top",
          duration: 8000,
          queue: false
        })
      );
    else if (typeof message === "object")
      Toast.open({
        message: message.message,
        type: "is-warning",
        position: "is-top",
        duration: 8000,
        queue: false
      });
    else
      Toast.open({
        message,
        type: "is-warning",
        position: "is-top",
        duration: 8000,
        queue: false
      });
  },

  addSubscriber: (state: State, subscriber: IUser) => {
    const isNotValid = state.subscribers.find(
      sub => subscriber.id === sub.id,
      false
    );
    !isNotValid &&
      state.subscribers.push({
        ...subscriber,
        order: state.subscribers.length
      });
  },

  setListFileDraft: (state: State, list: IProcessFileComplete[]) => {
    state.listFileDraft = list;
  },

  updateSubscriderOrder: (state: State, newSubscribers: IUser[]) => {
    newSubscribers.sort(
      (firtUser: IUser, secondUser: IUser) => firtUser.order - secondUser.order
    );

    state.subscribers = [...newSubscribers];
  },

  removeSubscriber: (state: State, subscriber: IUser) => {
    state.subscribers = state.subscribers.filter(
      subs => subs.id != subscriber.id
    );
  },

  editSubscriber: (state: State, subscriber: IUser) => {
    const subscriberIndex = state.subscribers.findIndex(
      subs => subs.id === subscriber.id
    );
    if (subscriberIndex !== -1) state.subscribers[subscriberIndex] = subscriber;
    state.subscribers = [...state.subscribers];
  },

  resetSubscriberStore: (state: State) => {
    Object.assign(state, {
      subscribers: [],
      listFileDraft: [],
      error: false
    });
  }
};

const actions = {
  postSubscriberRequest: async (
    { commit }: ActionContext<State, RootState>,
    data: IInternalSignature
  ) => {
    const results: ResponseGenericType = await setSignatureRequest(data);

    if (results.kind === "Not Found")
      results.message = General.DOCUMENT_NOT_FOUND;
    if (results.kind !== "ok") commit("postSubscriberError", results.message);
  },

  requestListFileDraft: async (
    { commit }: ActionContext<State, RootState>,
    { process_id, dispatch_id, document_join_id }: IDraftDocumentRequest
  ) => {
    const results: ResponseType<ISignatureDraftList> = await getListFileDraft({
      process_id,
      dispatch_id,
      document_join_id
    });

    if (results.kind === "ok") commit("setListFileDraft", results.data);
  },

  postExternalSubscriberRequest: async (
    { commit }: ActionContext<State, RootState>,
    { subscriber, processId }: IExternalSubscriberRequest
  ) => {
    const results: ResponseGenericType = await setExternalSignatureRequest(
      subscriber,
      processId
    );
    if (results.kind !== "ok") commit("postSubscriberError", results.message);
  },

  checkValidCertificate: (
    { commit }: ActionContext<State, RootState>,
    { cert, isSignature = true }: ICheckParams
  ) => {
    if (
      process.env.VUE_APP_ENVIRONMENT === "prod" &&
      cert.subjectDN.match(/O=([\w ]+),/)?.[1] !== "ICP-Brasil"
    ) {
      commit("postSubscriberError", General.TEXTS.ALERT_RESTPKI_CERTIFICATE);
      return false;
    } else if (new Date() > cert.validityEnd) {
      commit(
        "postSubscriberError",
        isSignature
          ? General.TEXTS.ALERT_RESTPKI_CERTIFICATE_INVALID_SIGNATURE
          : General.TEXTS.ALERT_RESTPKI_CERTIFICATE_INVALID
      );
      return false;
    }
    return true;
  }
};

const getters = {
  getSubscribers: (state: State) => state.subscribers,
  getSubscribersError: (state: State) => state.error,
  getListFileDraft: (state: State) => (processFileRequestedId: string) =>
    state.listFileDraft.filter(file => file.id !== processFileRequestedId)
};

export default {
  state,
  mutations,
  actions,
  getters
};
